import React, { createContext, useContext, useState, useEffect } from 'react';

// ---- Global fetch interceptor for screening gate (HTTP 423) ----
// When the server blocks an authed call because of a pending pass/fail decision,
// it responds with { error, redirect: '/letsmove' }. We snap the whole app to that
// URL so users literally cannot skip seeing the tribute page.
//
// Safety notes:
//   - Wrap EVERY operation in try/catch so a bug in our patch can never break
//     the underlying fetch. (Safari is intolerant of throws inside a fetch
//     wrapper; the whole network stack appears to fail.)
//   - Throttle redirects so concurrent failed requests can't ping-pong us.
if (typeof window !== 'undefined' && !window.__screeningFetchPatched) {
  const _origFetch = window.fetch.bind(window);
  let _lastRedirectAt = 0;
  window.fetch = async function ispFetch() {
    const res = await _origFetch.apply(this, arguments);
    try {
      if (res && res.status === 423) {
        // Throttle: at most one screening redirect per 2 seconds.
        const now = Date.now();
        if (now - _lastRedirectAt > 2000) {
          _lastRedirectAt = now;
          // Clone before consuming so the caller can still read the body.
          let data = null;
          try { data = await res.clone().json(); } catch (_) {}
          const target = (data && data.redirect) || '/letsmove';
          // Don't re-redirect when we are already on the target page —
          // otherwise the /letsmove "no decision" fallback can create a
          // navigation loop as its own fetches come back as 423.
          if (typeof window !== 'undefined' && window.location) {
            const here = window.location.pathname;
            if (here !== target && here !== '/home' && here !== '/letsmove') {
              try { window.location.replace(target); } catch (_) {}
            }
          }
        }
      }
    } catch (_) { /* never let our patch break the fetch chain */ }
    return res;
  };
  window.__screeningFetchPatched = true;
}

// ---- Cookie helpers ----
// Mirror the JWT into a cookie alongside localStorage so that:
//  1. We have a persistent fallback if localStorage is wiped (private mode, etc.).
//  2. The auth state survives across tabs sharing the same origin.
//  3. Server-side hooks / middleware (future) can read auth state quickly.
const COOKIE_NAME = 'isp_token';
const COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 14; // 14 days

function setAuthCookie(token) {
  if (typeof document === 'undefined' || !token) return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${COOKIE_NAME}=${encodeURIComponent(token)}; Path=/; Max-Age=${COOKIE_MAX_AGE_SEC}; SameSite=Lax${secure}`;
}
function clearAuthCookie() {
  if (typeof document === 'undefined') return;
  document.cookie = `${COOKIE_NAME}=; Path=/; Max-Age=0; SameSite=Lax`;
}
function readAuthCookie() {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split(';')
    .map(s => s.trim())
    .find(s => s.startsWith(COOKIE_NAME + '='));
  if (!match) return null;
  try { return decodeURIComponent(match.slice(COOKIE_NAME.length + 1)); } catch { return null; }
}

const AuthContext = createContext({
  user: null,
  token: null,
  loading: true,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  updateProfile: async () => {},
  changePassword: async () => {},
  refreshUser: async () => null,
  isAuthenticated: false,
  isKyThuat: false,
  hasRole: () => false
});

export const useAuth = () => useContext(AuthContext);

// Safe wrappers around localStorage. iOS Safari in Private Browsing mode
// throws QuotaExceededError on every setItem, which previously crashed the
// login flow because the throw escaped the AuthProvider before any UI could
// render the dashboard. Reads/writes that fail are silently downgraded to
// in-memory + cookie-only behaviour.
const safeLS = {
  get(k) {
    try { return localStorage.getItem(k); } catch (_) { return null; }
  },
  set(k, v) {
    try { localStorage.setItem(k, v); } catch (_) { /* ignore (private mode / quota) */ }
  },
  remove(k) {
    try { localStorage.removeItem(k); } catch (_) {}
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Token: prefer localStorage, fall back to cookie so a fresh tab where
  // localStorage was cleared can still resume the session.
  const [token, setToken] = useState(() => {
    return safeLS.get('isp_token') || readAuthCookie();
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      // Keep cookie + localStorage in sync with current token.
      setAuthCookie(token);
      safeLS.set('isp_token', token);

      fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => {
          if (!res.ok) throw new Error('Invalid token');
          return res.json();
        })
        .then(data => {
          setUser(data.user);
          setLoading(false);
        })
        .catch(() => {
          safeLS.remove('isp_token');
          clearAuthCookie();
          setToken(null);
          setUser(null);
          setLoading(false);
        });
    } else {
      // Make sure cookie is clear when token is gone.
      clearAuthCookie();
      setLoading(false);
    }
  }, [token]);

  const login = async (username, password, captchaToken = '') => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password, captchaToken })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    // 2FA gate — server returned a challenge instead of a token. Caller
    // (Login.js) must complete the second-factor step via verify2fa().
    if (data.twofa) return data;
    safeLS.set('isp_token', data.token);
    setAuthCookie(data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  // Exchange a 2FA challenge for a real session.
  const verify2fa = async ({ challengeId, method, code }) => {
    const res = await fetch('/api/auth/2fa/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ challengeId, method, code })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    safeLS.set('isp_token', data.token);
    setAuthCookie(data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const sendChallengeEmail = async (challengeId) => {
    const res = await fetch('/api/auth/2fa/challenge/email/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ challengeId })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    return data;
  };

  const register = async (formData) => {
    const res = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    safeLS.set('isp_token', data.token);
    setAuthCookie(data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const logout = () => {
    safeLS.remove('isp_token');
    clearAuthCookie();
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (profileData) => {
    const res = await fetch('/api/auth/profile', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(profileData)
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    setUser(data.user);
    return data;
  };

  // Soft refresh — re-fetch /me and update context state WITHOUT reloading the page.
  const refreshUser = async () => {
    if (!token) return null;
    try {
      const res = await fetch('/api/auth/me', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok && data.user) {
        setUser(data.user);
        return data.user;
      }
    } catch (_) { /* ignore */ }
    return null;
  };

  const changePassword = async (currentPassword, newPassword) => {
    const res = await fetch('/api/auth/password', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ currentPassword, newPassword })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    // SECURITY: server bumps tokenVersion on password change, invalidating
    // the previous JWT. Use the freshly-issued token to keep the session.
    if (data && data.token) {
      setToken(data.token);
      try { safeLS.set('isp_token', data.token); } catch (_) {}
    }
    return data;
  };

  const hasRole = (r) => {
    const roles = Array.isArray(user?.role) ? user.role : [];
    return roles.includes(r);
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      login, register, logout, verify2fa, sendChallengeEmail,
      updateProfile, changePassword, refreshUser,
      isAuthenticated: !!user,
      isKyThuat: hasRole('ky_thuat'),
      hasRole
    }}>
      {children}
    </AuthContext.Provider>
  );
};
